import { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import dayjs from "dayjs";
import DashboardLayout from "../../controls/DashboardLayout";
import DashboardNavbar from "../../controls/DashboardNavbar";
import MDBox from "../../controls/MDBox";
import { Grid } from "@mui/material";
import { Card } from "react-bootstrap";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import MDBadge from "../../controls/MDBadge";
import DataTable from "../../controls/Tables/DataTable";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";
import { getData, postData } from "../../Utils/Http/api";
import { fetchUsuarios } from "../../Utils/Usuarios";
import { fetchOrganismos } from "../../Utils/Organismo";

// Registro de búsquedas de casos (tabla busquedacasolog). Pantalla fuera de Perfiles y Permisos: el backend responde
// 403 a cualquier correo que no esté en Auditoria:EmailsAutorizados, y el menú solo se muestra si el backend lo permite.

const UMBRAL_LENTO_MS = 3000;

const PANTALLAS = [
  { value: "", label: "Todas" },
  { value: "PRE-JUDICIAL", label: "Pre-Judicial" },
  { value: "JUDICIAL", label: "Judicial" },
  { value: "CUALITATIVO", label: "Cualitativos" },
];

const CLASIFICACIONES = [
  { value: "", label: "Todas" },
  { value: "SIN_FILTROS", label: "Sin filtros" },
  { value: "AMPLIA", label: "Amplia" },
  { value: "ACOTADA", label: "Acotada" },
];

const ETIQUETA_CLASIFICACION = { SIN_FILTROS: "Sin filtros", AMPLIA: "Amplia", ACOTADA: "Acotada" };
const COLOR_CLASIFICACION = { SIN_FILTROS: "error", AMPLIA: "warning", ACOTADA: "success" };

const ETIQUETA_FILTRO = {
  periodo: "Período",
  organismo: "Organismo",
  gestor: "Gestor",
  caso: "N° caso",
  identificacion: "Identificación",
  nombre: "Nombre",
  nroExpediente: "Expediente",
  estado: "Estado",
  subEstado: "Sub estado",
  motivo: "Motivo",
  objetos: "Objetos",
  cantObjetosDesde: "Objetos desde",
  cantObjetosHasta: "Objetos hasta",
  montoDesde: "Monto desde",
  montoHasta: "Monto hasta",
  fechaDesde: "Fecha desde",
  fechaHasta: "Fecha hasta",
  poseeContactos: "Posee contactos",
  poseeCuit: "Posee CUIT",
  tipoCierre: "Cierre",
};

// El backend manda la fecha en UTC sin "Z": se interpreta como UTC y se muestra en hora local.
const aHoraLocal = (fechaUtc) => {
  if (!fechaUtc) return "";
  const texto = String(fechaUtc);
  return dayjs(new Date(/[zZ]|[+-]\d\d:\d\d$/.test(texto) ? texto : `${texto}Z`)).format("DD/MM/YYYY HH:mm:ss");
};

const hoy = () => dayjs().format("YYYY-MM-DD");
const haceDias = (dias) => dayjs().subtract(dias, "day").format("YYYY-MM-DD");

function BusquedaCasoLogListar() {
  const [acceso, setAcceso] = useState(null); // null = verificando, true / false
  const [filtros, setFiltros] = useState({
    desde: haceDias(7),
    hasta: hoy(),
    usuario: null,
    pantalla: PANTALLAS[0],
    clasificacion: CLASIFICACIONES[0],
    soloNoOptimas: false,
  });
  const [usuarios, setUsuarios] = useState([]);
  const [organismos, setOrganismos] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [detalle, setDetalle] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "info" });

  const nombreUsuario = useMemo(() => {
    const mapa = new Map(usuarios.map((u) => [u.id, u.label]));
    return (id) => mapa.get(id) || `ID ${id}`;
  }, [usuarios]);
  const nombreOrganismo = useMemo(() => {
    const mapa = new Map(organismos.map((o) => [o.idOrganismo, o.descripcion]));
    return (id) => mapa.get(id) || `ID ${id}`;
  }, [organismos]);

  const buscar = async (override = {}) => {
    const f = { ...filtros, ...override };
    setCargando(true);
    try {
      // El rango se arma en hora local y viaja como instantes UTC; el límite superior es exclusivo (día siguiente).
      const rango = {
        fechaDesdeUtc: f.desde ? dayjs(f.desde).startOf("day").toISOString() : null,
        fechaHastaUtc: f.hasta ? dayjs(f.hasta).add(1, "day").startOf("day").toISOString() : null,
        pantalla: f.pantalla?.value || "",
      };
      const [resumenRes, detalleRes] = await Promise.all([
        postData("/BusquedaCasoLogResumen", rango),
        postData("/BusquedaCasoLogListar", {
          ...rango,
          idUsuario: f.usuario?.id || 0,
          clasificacion: f.clasificacion?.value || "",
          soloNoOptimas: !!f.soloNoOptimas,
          limite: 500,
        }),
      ]);
      setResumen(resumenRes?.value || []);
      setDetalle(detalleRes?.value || []);
    } catch (ex) {
      if (ex?.response?.status === 403) {
        setAcceso(false);
      } else {
        console.error("Error cargando el registro de búsquedas:", ex);
        setSnackbar({ open: true, msg: ex?.message || "Error al cargar el registro de búsquedas", severity: "error" });
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    const iniciar = async () => {
      try {
        const res = await getData("/BusquedaCasoLogAcceso");
        const permitido = res?.value?.permitido === true;
        setAcceso(permitido);
        if (!permitido) return;
        const [listaUsuarios, listaOrganismos] = await Promise.all([fetchUsuarios(), fetchOrganismos()]);
        setUsuarios(listaUsuarios.map((u) => ({ id: u.idUsuario, label: u.nombreCompleto || u.usuario })));
        setOrganismos(listaOrganismos);
        await buscar();
      } catch (ex) {
        console.error("Error verificando el acceso:", ex);
        setAcceso(false);
      }
    };
    iniciar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerUsuario = (idUsuario) => {
    const usuario = usuarios.find((u) => u.id === idUsuario) || { id: idUsuario, label: `ID ${idUsuario}` };
    setFiltros((prev) => ({ ...prev, usuario }));
    buscar({ usuario });
  };

  const formatearValor = (clave, valor) => {
    if (clave === "gestor") return nombreUsuario(valor);
    if (clave === "organismo") return nombreOrganismo(valor);
    if (clave === "fechaDesde" || clave === "fechaHasta") return dayjs(valor).format("DD/MM/YYYY");
    if (clave === "poseeContactos" || clave === "poseeCuit") return valor === 1 ? "Sí" : "No";
    return String(valor);
  };

  const renderFiltros = (json) => {
    let activos = {};
    try {
      activos = JSON.parse(json || "{}");
    } catch (ex) {
      return <MDTypography variant="caption">{json}</MDTypography>;
    }
    const claves = Object.keys(activos);
    if (claves.length === 0) {
      return (
        <MDTypography variant="caption" color="error" fontWeight="bold">
          Sin filtros
        </MDTypography>
      );
    }
    return (
      <MDBox lineHeight={1.3}>
        {claves.map((clave) => (
          <MDTypography key={clave} variant="caption" display="block" color="text">
            <strong>{ETIQUETA_FILTRO[clave] || clave}:</strong> {formatearValor(clave, activos[clave])}
          </MDTypography>
        ))}
      </MDBox>
    );
  };

  const columnasResumen = [
    { Header: "Usuario", accessor: "usuario", align: "left", width: "22%" },
    { Header: "Búsquedas", accessor: "busquedas", align: "center" },
    { Header: "Sin filtros", accessor: "sinFiltros", align: "center" },
    { Header: "Amplias", accessor: "amplias", align: "center" },
    { Header: "No óptimas", accessor: "noOptimas", align: "center" },
    { Header: "Llegaron al límite", accessor: "limite", align: "center" },
    { Header: "Fallidas", accessor: "fallidas", align: "center" },
    { Header: "Duración (prom. / máx.)", accessor: "duracion", align: "center" },
    { Header: "Última búsqueda", accessor: "ultima", align: "left" },
    { Header: "", accessor: "ver", align: "center" },
  ];

  const filasResumen = resumen.map((r) => {
    const peor = r.pctNoOptimas >= 50;
    return {
      usuario: (
        <MDTypography variant="caption" fontWeight="medium" color="text">
          {r.usuario}
        </MDTypography>
      ),
      busquedas: <MDTypography variant="caption">{r.busquedas}</MDTypography>,
      sinFiltros: (
        <MDTypography variant="caption" color={r.sinFiltros > 0 ? "error" : "text"} fontWeight={r.sinFiltros > 0 ? "bold" : "regular"}>
          {r.sinFiltros}
        </MDTypography>
      ),
      amplias: (
        <MDTypography variant="caption" color={r.amplias > 0 ? "warning" : "text"}>
          {r.amplias}
        </MDTypography>
      ),
      noOptimas: (
        <MDTypography variant="caption" color={peor ? "error" : "text"} fontWeight={peor ? "bold" : "regular"}>
          {r.noOptimas} ({r.pctNoOptimas}%)
        </MDTypography>
      ),
      limite: <MDTypography variant="caption">{r.llegaronAlLimite}</MDTypography>,
      fallidas: (
        <MDTypography variant="caption" color={r.fallidas > 0 ? "error" : "text"}>
          {r.fallidas}
        </MDTypography>
      ),
      duracion: (
        <MDTypography variant="caption" color={r.maxMs > UMBRAL_LENTO_MS ? "warning" : "text"}>
          {r.promedioMs} ms / {r.maxMs} ms
        </MDTypography>
      ),
      ultima: <MDTypography variant="caption">{aHoraLocal(r.ultimaBusquedaUtc)}</MDTypography>,
      ver: (
        <IconButton size="small" title="Ver sus búsquedas" onClick={() => handleVerUsuario(r.idUsuario)}>
          <VisibilityIcon color="info" fontSize="small" />
        </IconButton>
      ),
    };
  });

  const columnasDetalle = [
    { Header: "Fecha", accessor: "fecha", align: "left" },
    { Header: "Usuario", accessor: "usuario", align: "left" },
    { Header: "Pantalla", accessor: "pantalla", align: "left" },
    { Header: "Filtros usados", accessor: "filtros", align: "left", width: "28%" },
    { Header: "Clasificación", accessor: "clasificacion", align: "center" },
    { Header: "Duración", accessor: "duracion", align: "center" },
    { Header: "Resultados", accessor: "resultados", align: "center" },
    { Header: "Óptima", accessor: "optima", align: "center" },
  ];

  const filasDetalle = detalle.map((d) => ({
    fecha: <MDTypography variant="caption">{aHoraLocal(d.fechaUtc)}</MDTypography>,
    usuario: (
      <MDTypography variant="caption" fontWeight="medium" color="text">
        {d.usuario}
      </MDTypography>
    ),
    pantalla: (
      <MDBox lineHeight={1.2}>
        <MDTypography variant="caption" display="block" fontWeight="medium" color="text">
          {PANTALLAS.find((p) => p.value === d.pantalla)?.label || d.pantalla}
        </MDTypography>
        <MDTypography variant="caption" display="block" color="secondary">
          {d.endpoint}
        </MDTypography>
      </MDBox>
    ),
    filtros: renderFiltros(d.filtros),
    clasificacion: (
      <MDBadge
        badgeContent={ETIQUETA_CLASIFICACION[d.clasificacion] || d.clasificacion}
        color={COLOR_CLASIFICACION[d.clasificacion] || "secondary"}
        variant="gradient"
        size="sm"
      />
    ),
    duracion: (
      <MDTypography variant="caption" color={d.duracionMs > UMBRAL_LENTO_MS ? "error" : "text"} fontWeight={d.duracionMs > UMBRAL_LENTO_MS ? "bold" : "regular"}>
        {d.duracionMs} ms
      </MDTypography>
    ),
    resultados: (
      <MDBox lineHeight={1.2}>
        <MDTypography variant="caption" display="block" color="text">
          {d.cantidadResultados} (límite {d.limiteRegistros})
        </MDTypography>
        {d.llegoAlLimite && (
          <MDTypography variant="caption" display="block" color="warning" fontWeight="bold">
            Llegó al límite
          </MDTypography>
        )}
        {!d.exitosa && (
          <MDTypography variant="caption" display="block" color="error" fontWeight="bold" title={d.error || ""}>
            Falló
          </MDTypography>
        )}
      </MDBox>
    ),
    optima: d.esOptima ? <CheckCircle color="success" fontSize="small" /> : <Cancel color="error" fontSize="small" />,
  }));

  if (acceso === null) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (acceso === false) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} px={3}>
          <MDTypography variant="h6" color="text">
            No tenés acceso a esta pantalla.
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="dark" borderRadius="lg" coloredShadow="dark">
                <MDTypography variant="h6" color="white">
                  Registro de Búsquedas de Casos
                </MDTypography>
              </MDBox>

              <MDBox pt={3} pb={1} px={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <TextField
                  type="date"
                  label="Desde"
                  value={filtros.desde}
                  onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 170 }}
                />
                <TextField
                  type="date"
                  label="Hasta"
                  value={filtros.hasta}
                  onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 170 }}
                />
                <Autocomplete
                  options={usuarios}
                  getOptionLabel={(o) => o.label || ""}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={filtros.usuario}
                  onChange={(e, valor) => setFiltros({ ...filtros, usuario: valor })}
                  renderInput={(params) => <TextField {...params} label="Usuario" />}
                  sx={{ width: 260 }}
                />
                <Autocomplete
                  options={PANTALLAS}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  value={filtros.pantalla}
                  disableClearable
                  onChange={(e, valor) => setFiltros({ ...filtros, pantalla: valor })}
                  renderInput={(params) => <TextField {...params} label="Pantalla" />}
                  sx={{ width: 190 }}
                />
                <Autocomplete
                  options={CLASIFICACIONES}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  value={filtros.clasificacion}
                  disableClearable
                  onChange={(e, valor) => setFiltros({ ...filtros, clasificacion: valor })}
                  renderInput={(params) => <TextField {...params} label="Clasificación" />}
                  sx={{ width: 190 }}
                />
                <FormControlLabel
                  control={<Checkbox checked={filtros.soloNoOptimas} onChange={(e) => setFiltros({ ...filtros, soloNoOptimas: e.target.checked })} />}
                  label="Solo no óptimas"
                />
                <MDButton variant="gradient" color="info" startIcon={<SearchIcon />} onClick={() => buscar()} disabled={cargando}>
                  Buscar
                </MDButton>
              </MDBox>

              <MDBox px={2} pb={1}>
                <MDTypography variant="caption" color="text">
                  Fechas en hora local. Óptima: búsqueda acotada (período, organismo, gestor, caso, identificación, nombre o expediente),
                  exitosa y de menos de {UMBRAL_LENTO_MS / 1000} segundos. El resumen respeta las fechas y la pantalla; el detalle muestra hasta 500 búsquedas.
                </MDTypography>
              </MDBox>

              {cargando ? (
                <MDBox display="flex" justifyContent="center" py={6}>
                  <CircularProgress />
                </MDBox>
              ) : (
                <>
                  <MDBox pt={2} px={2}>
                    <MDTypography variant="h6" color="text">
                      Resumen por usuario
                    </MDTypography>
                  </MDBox>
                  <DataTable
                    table={{ columns: columnasResumen, rows: filasResumen }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries
                    canSearch={false}
                    noEndBorder
                    pagination={{ color: "info", variant: "gradient" }}
                  />

                  <MDBox pt={3} px={2}>
                    <MDTypography variant="h6" color="text">
                      Detalle de búsquedas
                    </MDTypography>
                  </MDBox>
                  <DataTable
                    table={{ columns: columnasDetalle, rows: filasDetalle }}
                    isSorted={false}
                    entriesPerPage={true}
                    showTotalEntries
                    canSearch={false}
                    noEndBorder
                    pagination={{ color: "info", variant: "gradient" }}
                  />
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <MuiAlert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} elevation={6} variant="filled">
          {snackbar.msg}
        </MuiAlert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default BusquedaCasoLogListar;
